import React, { useMemo } from "react";

// A bare trend shape for a KPI tile: no axes, no grid, no labels, no tooltip.
// Hand-rolled SVG rather than @mui/x-charts because a chart component at this
// size spends its budget on chrome we explicitly do not want, and renders a
// wrapper per tile.
//
// It is decorative by design — the number and delta beside it carry the
// meaning — so it is aria-hidden rather than given a label a screen reader
// would have to wade through.
// 92px of width cannot resolve 90 daily points: at ~1px per day the line turns
// into a picket fence that says "noisy" and nothing else. Past this many points
// the days are summed into equal buckets — summed, not averaged, because these
// are counts and a bucket's height should still mean "how much happened here".
// 30 days and below are untouched, so the common view is still day-by-day.
const MAX_POINTS = 26;

const bucket = (values) => {
  if (values.length <= MAX_POINTS) return values;
  const size = Math.ceil(values.length / MAX_POINTS);
  const out = [];
  for (let i = 0; i < values.length; i += size) {
    out.push(values.slice(i, i + size).reduce((sum, v) => sum + v, 0));
  }
  return out;
};

const Sparkline = ({ values = [], color, width = 92, height = 38 }) => {
  // One gradient def per instance, or four tiles would share an id and the
  // first one's stops would win for all of them.
  const gradientId = useMemo(
    () => `spark-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );

  const path = useMemo(() => {
    const points = bucket(values.filter((v) => typeof v === "number"));
    if (points.length < 2) return null;

    const max = Math.max(...points);
    const min = Math.min(...points);
    // A flat series would divide by zero; draw it along the baseline instead.
    const span = max - min || 1;
    const stepX = width / (points.length - 1);
    // Inset by the stroke width so the first and last points are not clipped.
    const top = 1.5;
    const usable = height - top * 2;

    const coords = points.map((v, i) => {
      const x = i * stepX;
      const y = top + usable - ((v - min) / span) * usable;
      return [x, y];
    });

    return {
      line: coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" "),
      area:
        `M0,${height} ` +
        coords.map(([x, y]) => `L${x.toFixed(1)},${y.toFixed(1)}`).join(" ") +
        ` L${width},${height} Z`,
      flat: max === min,
    };
  }, [values, width, height]);

  if (!path) return <svg width={width} height={height} aria-hidden="true" />;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.28} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      {!path.flat && (
        <path
          d={path.area}
          fill={`url(#${gradientId})`}
          style={{ transition: "d 420ms cubic-bezier(0.4, 0, 0.2, 1)" }}
        />
      )}
      <path
        d={path.line}
        // Chrome can tween the `d` property, so a range change redraws the
        // trend as a moving line rather than a jump cut. Where the browser
        // cannot (or the viewer asked for less motion) it simply snaps, which
        // is the behaviour this had before.
        style={{ transition: "d 420ms cubic-bezier(0.4, 0, 0.2, 1)" }}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity={path.flat ? 0.35 : 1}
      />
    </svg>
  );
};

export default Sparkline;
